/* eslint-disable @typescript-eslint/no-explicit-any */

// TODO: Fix ESLint errors and improve type safety

import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import { useLocationStore } from '@/features/location/store/location-store';
import { useChargePointsStore } from '@/features/charge-points/store/charge-points-store';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { useGoogleMapsStore } from '@/features/map/store/google-maps-store';
import { useLocaleStore } from '@/features/locale/store/locale-store';
import { useOperatorStore } from '@/features/operator/store/operator-store';
import { useUiModeStore } from '@/features/ui-mode/store/ui-mode-store';

/**
 * Debounce utility for URL updates
 */
function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Reusable param schemas
 */
// Hex color (6 chars, optional leading #). Invalid values are silently ignored.
const hexColorParam = z
  .string()
  .regex(/^#?[0-9a-fA-F]{6}$/)
  .transform((v) => (v.startsWith('#') ? v : `#${v}`))
  .optional()
  .catch(undefined);
// Boolean "show/hide" flag. Invalid values fall back to undefined (→ default shown).
const boolParam = z
  .enum(['true', 'false', '1', '0'])
  .transform((val) => val === 'true' || val === '1')
  .optional()
  .catch(undefined);
// Marker size (T-shirt sizes). `m` = medium = automatic count-based sizing.
// Invalid values fall back to undefined (→ count-based).
const markerSizeParam = z.enum(['xs', 's', 'm']).optional().catch(undefined);

/**
 * URL parameters schema
 */
const urlParamsShape = {
  // Location
  lat: z.coerce.number().optional(),
  lng: z.coerce.number().optional(),
  // Clamp to the map's own zoom range (GoogleMapView minZoom/maxZoom).
  zoom: z.coerce
    .number()
    .transform((zoom) => Math.min(17, Math.max(5, zoom)))
    .optional(),
  // Charge points
  chargePointId: z.coerce.number().optional(),
  siteId: z.coerce.number().optional(),
  // UI Controls
  controls: z
    .string()
    .transform((val) => val === 'true' || val === '1')
    .optional(),
  filter_ui: z
    .string()
    .transform((val) => val === '1' || val === 'true')
    .optional(),
  filter_operator_ui: z
    .string()
    .transform((val) => val === '1' || val === 'true')
    .optional(),
  anchor_point: z
    .string()
    .transform((val) => val === 'true' || val === '1')
    .optional(),
  best_price: boolParam,
  location_details: boolParam,
  // UI-visibility flags (inbound-only, default shown)
  filter_price_ui: boolParam,
  share_ui: boolParam,
  // Locale
  locale: z.enum(['en', 'en-US', 'da', 'es', 'fr', 'de']).optional(),
  language_mode: z.enum(['fixed']).optional(),
  // Map Style
  map_style: z.enum(['default', 'silver']).optional(),
  marker_style: z.enum(['default', 'operator', 'operator_neutral']).optional(),
  // Operator
  operator: z.string().optional(),
  operator_scope: z.enum(['restrict']).optional(),
  // Filters - Availability
  // Use custom transform to handle "0"/"1" strings correctly
  onlyAvailable: z
    .string()
    .transform((val) => val === '1' || val === 'true')
    .optional(),
  // Filters - Speed
  slow: z
    .string()
    .transform((val) => val === '1' || val === 'true')
    .optional(),
  fast: z
    .string()
    .transform((val) => val === '1' || val === 'true')
    .optional(),
  ultraFast: z
    .string()
    .transform((val) => val === '1' || val === 'true')
    .optional(),
  // Filters - Operators
  operatorIds: z.string().optional(),
  teamIds: z.string().optional(),
  promotionIds: z.string().optional(),
  // Filters - Numeric
  maxPrice: z.coerce.number().optional(),
  // Map data type (inbound-only, not round-tripped). Drives charge-points vs
  // live charge-sessions rendering (BIZOPS-1588). Invalid → undefined.
  mapType: z
    .enum(['chargePoints', 'chargeSessions'])
    .optional()
    .catch(undefined),
  // UI Mode & Styling
  ui_mode: z.enum(['default', 'operator']).optional().catch(undefined),
  tooltip_style: z.enum(['default', 'light']).optional().catch(undefined),
  loaderColor: hexColorParam,
  logoBgColor: hexColorParam, // operator-logo background override
  // Marker styling (inbound-only, not round-tripped). Applies to clusters AND single pins.
  marker_color: hexColorParam, // center/count circle background accent
  marker_color_available: hexColorParam, // available ring segment + green pin border
  marker_color_busy: hexColorParam, // busy ring segment + orange pin border
  marker_color_disconnected: hexColorParam, // disconnected ring segment + grey pin border
  marker_color_error: hexColorParam, // error ring segment + red pin border
  country_highlight_color: hexColorParam, // country-overlay hover highlight
  country_fill_color: hexColorParam, // country-overlay resting fill tint
  country_fill_opacity: z.coerce
    .number()
    .min(0)
    .max(1)
    .optional()
    .catch(undefined), // country-overlay resting fill opacity (0–1)
  marker_size: markerSizeParam,
};

// Every param falls back to undefined on its own, so one bad value (e.g.
// ?zoom=abc, ?locale=nl, ?chargePointId=abc) is ignored instead of throwing
// during render and crashing the whole app.
export const urlParamsSchema = z.object(
  Object.fromEntries(
    Object.entries(urlParamsShape).map(([key, schema]) => [
      key,
      (schema as z.ZodTypeAny).catch(undefined),
    ])
  ) as unknown as typeof urlParamsShape
);

export type UrlParams = z.infer<typeof urlParamsSchema>;

/**
 * Hook to manage URL parameters and sync with stores
 */
export function useUrlParams() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef(false);
  const hasNavigatedToUrl = useRef(false);
  // Capture initial URL params once to avoid feedback loops
  const initialUrlParams = useRef<URLSearchParams | null>(null);

  if (!initialUrlParams.current) {
    initialUrlParams.current = new URLSearchParams(searchParams.toString());
  }

  // Get current store values for syncing
  const mapCenter = useLocationStore((s) => s.mapCenter);
  const zoom = useLocationStore((s) => s.zoomLevel);
  const mapInstance = useGoogleMapsStore((s) => s.mapInstance);
  const currentLocale = useLocaleStore((s) => s.currentLocale);
  const operator = useOperatorStore((s) => s.operator);
  const selectedChargePoint = useChargePointsStore(
    (s) => s.selectedChargePoint
  );
  // A deep link counts as loading from the moment it's read from the URL
  // (it waits for guest auth before fetching) until it resolves or closes
  const isLoadingSelection = useChargePointsStore(
    (s) =>
      s.isLoadingDetails ||
      s.isLoadingGroup ||
      (!s.selectedChargePoint &&
        (s.urlChargePointId !== undefined ||
          s.urlChargePointSiteId !== undefined))
  );

  // Get filter store values (individual selectors for proper reactivity)
  const controlsEnabled = useFilterStore((s) => s.controlsEnabled);
  const isFilterPanelOpen = useFilterStore((s) => s.isFilterPanelOpen);
  const showOperatorFilter = useFilterStore((s) => s.showOperatorFilter);
  const showAnchorPoint = useFilterStore((s) => s.showAnchorPoint);
  const markerStyle = useFilterStore((s) => s.markerStyle);
  const showOnlyAvailable = useFilterStore((s) => s.showOnlyAvailable);
  const showSlow = useFilterStore((s) => s.showSlow);
  const showFast = useFilterStore((s) => s.showFast);
  const showUltraFast = useFilterStore((s) => s.showUltraFast);
  const selectedOperatorIds = useFilterStore((s) => s.selectedOperatorIds); // Actual operator IDs (e.g., 602)
  const selectedTeamIds = useFilterStore((s) => s.selectedTeamIds); // Actual team IDs (e.g., 182)
  const selectedPromotionIds = useFilterStore((s) => s.selectedPromotionIds); // Promotion IDs
  const maxPricePerKwh = useFilterStore((s) => s.maxPricePerKwh);

  // Parse URL params (convert null to undefined for proper optional handling)
  const params = urlParamsSchema.parse({
    lat: searchParams.get('lat') ?? undefined,
    lng: searchParams.get('lng') ?? undefined,
    zoom: searchParams.get('zoom') ?? undefined,
    chargePointId: searchParams.get('chargePointId') ?? undefined,
    siteId: searchParams.get('siteId') ?? undefined,
    controls: searchParams.get('controls') ?? undefined,
    filter_ui: searchParams.get('filter_ui') ?? undefined,
    filter_operator_ui: searchParams.get('filter_operator_ui') ?? undefined,
    anchor_point: searchParams.get('anchor_point') ?? undefined,
    best_price: searchParams.get('best_price') ?? undefined,
    location_details: searchParams.get('location_details') ?? undefined,
    locale: searchParams.get('locale') ?? undefined,
    language_mode: searchParams.get('language_mode') ?? undefined,
    map_style: searchParams.get('map_style') ?? undefined,
    marker_style: searchParams.get('marker_style') ?? undefined,
    operator: searchParams.get('operator') ?? undefined,
    operator_scope: searchParams.get('operator_scope') ?? undefined,
    // Filter params
    onlyAvailable: searchParams.get('onlyAvailable') ?? undefined,
    slow: searchParams.get('slow') ?? undefined,
    fast: searchParams.get('fast') ?? undefined,
    ultraFast: searchParams.get('ultraFast') ?? undefined,
    operatorIds: searchParams.get('operatorIds') ?? undefined,
    teamIds: searchParams.get('teamIds') ?? undefined,
    promotionIds: searchParams.get('promotionIds') ?? undefined,
    maxPrice: searchParams.get('maxPrice') ?? undefined,
    mapType: searchParams.get('mapType') ?? undefined,
    ui_mode: searchParams.get('ui_mode') ?? undefined,
    tooltip_style: searchParams.get('tooltip_style') ?? undefined,
    loaderColor: searchParams.get('loaderColor') ?? undefined,
    logoBgColor: searchParams.get('logoBgColor') ?? undefined,
    marker_color: searchParams.get('marker_color') ?? undefined,
    marker_color_available:
      searchParams.get('marker_color_available') ?? undefined,
    marker_color_busy: searchParams.get('marker_color_busy') ?? undefined,
    marker_color_disconnected:
      searchParams.get('marker_color_disconnected') ?? undefined,
    marker_color_error: searchParams.get('marker_color_error') ?? undefined,
    country_highlight_color:
      searchParams.get('country_highlight_color') ?? undefined,
    country_fill_color: searchParams.get('country_fill_color') ?? undefined,
    country_fill_opacity: searchParams.get('country_fill_opacity') ?? undefined,
    marker_size: searchParams.get('marker_size') ?? undefined,
    filter_price_ui: searchParams.get('filter_price_ui') ?? undefined,
    share_ui: searchParams.get('share_ui') ?? undefined,
  });

  // Sync URL params to stores on mount
  useEffect(() => {
    const filterStoreInstance = useFilterStore.getState();
    const locationStore = useLocationStore.getState();

    // Location params
    if (params.lat !== undefined && params.lng !== undefined) {
      locationStore.updateUrlParams({
        lat: params.lat,
        lng: params.lng,
        zoom: params.zoom,
      });
      // Navigate to URL location (sets mapCenter and zoomLevel)
      locationStore.navigateToUrlLocation();
    } else {
      // No URL location params - initialize with smart defaults
      // Priority: IP location > Denmark default
      void locationStore.initializeLocation();
    }

    // Charge point params
    if (params.chargePointId !== undefined) {
      useChargePointsStore.getState().setUrlChargePointId(params.chargePointId);
    }
    if (params.siteId !== undefined) {
      useChargePointsStore.getState().setUrlChargePointSiteId(params.siteId);
    }

    // Filter params - Availability
    // Only update if URL value differs from current state
    if (
      params.onlyAvailable !== undefined &&
      params.onlyAvailable !== filterStoreInstance.showOnlyAvailable
    ) {
      filterStoreInstance.setShowOnlyAvailable(params.onlyAvailable);
    }
    // Filter params - Speed
    if (
      params.slow !== undefined &&
      params.slow !== filterStoreInstance.showSlow
    ) {
      filterStoreInstance.toggleChargeSpeed('slow');
    }
    if (
      params.fast !== undefined &&
      params.fast !== filterStoreInstance.showFast
    ) {
      filterStoreInstance.toggleChargeSpeed('fast');
    }
    if (
      params.ultraFast !== undefined &&
      params.ultraFast !== filterStoreInstance.showUltraFast
    ) {
      filterStoreInstance.toggleChargeSpeed('ultraFast');
    }

    // Filter params - Operators
    // Apply operator IDs directly from URL (global operator IDs that work everywhere)
    if (params.operatorIds !== undefined) {
      const operatorIds = params.operatorIds
        .split(',')
        .map(Number)
        .filter((id) => !isNaN(id));
      if (operatorIds.length > 0) {
        // Set operator IDs directly - these will be sent to the charge points API
        useFilterStore.setState({ selectedOperatorIds: operatorIds });
      }
    }

    // Filter params - Team IDs
    // Apply team IDs directly from URL (global team IDs that work everywhere)
    if (params.teamIds !== undefined) {
      const teamIds = params.teamIds
        .split(',')
        .map(Number)
        .filter((id) => !isNaN(id));
      if (teamIds.length > 0) {
        // Set team IDs directly - these will be sent to the charge points API
        useFilterStore.setState({ selectedTeamIds: teamIds });
      }
    }

    // Filter params - Promotion IDs
    // Apply promotion IDs directly from URL
    if (params.promotionIds !== undefined) {
      const promotionIds = params.promotionIds
        .split(',')
        .map(Number)
        .filter((id) => !isNaN(id));
      if (promotionIds.length > 0) {
        // Set promotion IDs directly - these will be sent to the charge points API
        useFilterStore.setState({ selectedPromotionIds: promotionIds });
      }
    }

    // operator_scope=restrict means the host page owns the scope and we hide the
    // operator filter, so pin it as the reset baseline. Without this, "Reset
    // filters" drops the scope and the embed falls back to every charge point
    // with no UI left to restore it (e.g. the Norlys discount map).
    if (params.operator_scope === 'restrict') {
      const { selectedOperatorIds, selectedTeamIds, selectedPromotionIds } =
        useFilterStore.getState();
      filterStoreInstance.setUrlScope({
        operatorIds: [...selectedOperatorIds],
        teamIds: [...selectedTeamIds],
        promotionIds: [...selectedPromotionIds],
      });
    }

    // Filter params - Numeric
    if (params.maxPrice !== undefined) {
      filterStoreInstance.setMaxPrice(params.maxPrice);
    }

    // Controls param - ONLY 'false' string disables controls (matches Flutter behavior)
    const controlsParam = searchParams.get('controls');
    if (controlsParam === 'false') {
      filterStoreInstance.setControlsEnabled(false);
    }

    // Filter UI param - viewport-aware default
    // Explicit values override viewport defaults
    if (params.filter_ui === true) {
      // Explicitly open
      useFilterStore.setState({ isFilterPanelOpen: true });
    } else if (params.filter_ui === false) {
      // Explicitly close
      useFilterStore.setState({ isFilterPanelOpen: false });
    } else {
      // Not specified - closed on every viewport; the quick filter chips
      // over the map cover the common filters
      useFilterStore.setState({ isFilterPanelOpen: false });
    }

    // Filter Operator UI param (default: true, only 'false' or '0' hides it)
    if (params.filter_operator_ui === false) {
      useFilterStore.setState({ showOperatorFilter: false });
    }

    // Anchor Point param (default: true, only 'false' or '0' hides it)
    if (params.anchor_point === false) {
      useFilterStore.setState({ showAnchorPoint: false });
    }

    // Marker Style param (default: 'default', can be 'operator')
    if (params.marker_style) {
      useFilterStore.setState({ markerStyle: params.marker_style });
    }

    // Locale param
    if (params.locale) {
      useLocaleStore.getState().setLocale(params.locale);
    }

    // Note: Operator initialization is handled by:
    // 1. App.tsx on first mount (default Monta operator)
    // 2. Operator watcher effect below (handles URL changes)
    // This prevents redundant API calls on initial load.

    // Note: operator= and operatorIds= are independent parameters:
    // - operator=norlys → Loads Norlys branding/theme only (no filtering)
    // - operatorIds=602 → Filters charge points by operator ID (no theme)
    // - operator=norlys&operatorIds=602 → Both theme AND filtering

    // UI Mode param
    if (params.ui_mode) {
      useUiModeStore.getState().setUiMode(params.ui_mode);
    }

    // Tooltip Style param
    if (params.tooltip_style) {
      useUiModeStore.getState().setTooltipStyle(params.tooltip_style);
    }

    // Loader Color param
    if (params.loaderColor) {
      useUiModeStore.getState().setLoaderColor(params.loaderColor);
    }

    // Logo background Color param
    if (params.logoBgColor) {
      useUiModeStore.getState().setLogoBgColor(params.logoBgColor);
    }

    // Marker customization params (inbound-only)
    if (params.marker_color) {
      useUiModeStore.getState().setMarkerColor(params.marker_color);
    }
    if (params.marker_color_available) {
      useUiModeStore
        .getState()
        .setMarkerColorAvailable(params.marker_color_available);
    }
    if (params.marker_color_busy) {
      useUiModeStore.getState().setMarkerColorBusy(params.marker_color_busy);
    }
    if (params.marker_color_disconnected) {
      useUiModeStore
        .getState()
        .setMarkerColorDisconnected(params.marker_color_disconnected);
    }
    if (params.marker_color_error) {
      useUiModeStore.getState().setMarkerColorError(params.marker_color_error);
    }
    if (params.marker_size) {
      useUiModeStore.getState().setMarkerSize(params.marker_size);
    }
    if (params.country_highlight_color) {
      useUiModeStore
        .getState()
        .setCountryHighlightColor(params.country_highlight_color);
    }
    if (params.country_fill_color) {
      useUiModeStore.getState().setCountryFillColor(params.country_fill_color);
    }
    if (params.country_fill_opacity !== undefined) {
      useUiModeStore
        .getState()
        .setCountryFillOpacity(params.country_fill_opacity);
    }

    // Mark as initialized after reading URL params
    isInitialized.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Pan map to URL location once map instance is ready
  useEffect(() => {
    if (!mapInstance || hasNavigatedToUrl.current) return;

    const { urlParams } = useLocationStore.getState();
    if (urlParams.lat && urlParams.lng) {
      mapInstance.panTo({ lat: urlParams.lat, lng: urlParams.lng });
      if (urlParams.zoom) {
        mapInstance.setZoom(urlParams.zoom);
      }
      hasNavigatedToUrl.current = true;
    }
  }, [mapInstance]);

  /**
   * Update URL with current app state
   */
  const updateUrl = () => {
    const location = useLocationStore.getState();
    const chargePoint = useChargePointsStore.getState();

    const newParams = new URLSearchParams();

    // Add location params
    if (location.mapCenter) {
      newParams.set('lat', location.mapCenter.lat.toFixed(7));
      newParams.set('lng', location.mapCenter.lng.toFixed(7));
      newParams.set('zoom', location.zoomLevel.toFixed(1));
    }

    // Add charge point params
    if (chargePoint.selectedChargePoint) {
      if (chargePoint.selectedChargePoint.type === 'chargePoint') {
        newParams.set('cpId', chargePoint.selectedChargePoint.id);
      } else {
        newParams.set('cpSiteId', chargePoint.selectedChargePoint.id);
      }
    }

    setSearchParams(newParams, { replace: true });
  };

  // Auto-update URL when map center or zoom changes (debounced)
  useEffect(() => {
    // Don't update URL until after initial params are read
    if (!isInitialized.current) return;
    if (!mapCenter) return;

    // A fresh debounce() per run never cancelled the previous one, so every
    // center change while panning still wrote the URL. Clear it instead.
    const timeout = setTimeout(() => {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);

          // Update location params
          newParams.set('lat', mapCenter.lat.toFixed(7));
          newParams.set('lng', mapCenter.lng.toFixed(7));
          newParams.set('zoom', zoom.toFixed(1));

          return newParams;
        },
        { replace: true }
      );
    }, 500);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenter, zoom]);

  // Sync best_price param to store whenever it changes in the URL
  // Handles both initial load and client-side navigation (e.g. setSearchParams)
  useEffect(() => {
    // params.best_price is undefined when absent (no param), false when explicitly '0'/'false'
    const show = params.best_price !== false; // true by default when absent
    useFilterStore.setState({ showBestPriceButton: show });
  }, [params.best_price]);

  // Sync location_details param to store whenever it changes in the URL
  // location_details=false hides the filter sections block; absent/true shows it (default)
  useEffect(() => {
    // params.location_details is undefined when absent (no param), false when explicitly '0'/'false'
    const show = params.location_details !== false; // true by default when absent
    useFilterStore.setState({ showLocationDetails: show });
  }, [params.location_details]);

  // Sync filter_price_ui param to store whenever it changes in the URL
  // filter_price_ui=false/0 hides the price filter section; absent/true shows it (default)
  useEffect(() => {
    const show = params.filter_price_ui !== false; // true by default when absent
    useFilterStore.setState({ showPriceFilter: show });
  }, [params.filter_price_ui]);

  // Sync share_ui param to store whenever it changes in the URL
  // share_ui=false/0 hides the Share button; absent/true shows it (default)
  useEffect(() => {
    const show = params.share_ui !== false; // true by default when absent
    useFilterStore.setState({ showShareButton: show });
  }, [params.share_ui]);

  // Sync ui_mode param to store whenever it changes in the URL
  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore.getState().setUiMode(params.ui_mode ?? 'default');
  }, [params.ui_mode]);

  // Sync tooltip_style param to store whenever it changes in the URL
  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setTooltipStyle(params.tooltip_style ?? 'default');
  }, [params.tooltip_style]);

  // Sync loaderColor param to store whenever it changes in the URL
  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore.getState().setLoaderColor(params.loaderColor ?? null);
  }, [params.loaderColor]);

  // Sync logoBgColor param to store whenever it changes in the URL
  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore.getState().setLogoBgColor(params.logoBgColor ?? null);
  }, [params.logoBgColor]);

  // Sync marker customization params to store whenever they change in the URL
  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore.getState().setMarkerColor(params.marker_color ?? null);
  }, [params.marker_color]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setMarkerColorAvailable(params.marker_color_available ?? null);
  }, [params.marker_color_available]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setMarkerColorBusy(params.marker_color_busy ?? null);
  }, [params.marker_color_busy]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setMarkerColorDisconnected(params.marker_color_disconnected ?? null);
  }, [params.marker_color_disconnected]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setMarkerColorError(params.marker_color_error ?? null);
  }, [params.marker_color_error]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setCountryHighlightColor(params.country_highlight_color ?? null);
  }, [params.country_highlight_color]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setCountryFillColor(params.country_fill_color ?? null);
  }, [params.country_fill_color]);

  useEffect(() => {
    if (!isInitialized.current) return;
    useUiModeStore
      .getState()
      .setCountryFillOpacity(params.country_fill_opacity ?? null);
  }, [params.country_fill_opacity]);

  useEffect(() => {
    if (!isInitialized.current) return;
    // Default to small markers when no override is provided.
    useUiModeStore.getState().setMarkerSize(params.marker_size ?? 's');
  }, [params.marker_size]);

  // Watch for operator parameter changes (after initialization)
  useEffect(() => {
    // Don't run until URL params are initialized
    if (!isInitialized.current) return;

    // Wait for operator store to be initialized by App.tsx first
    // This prevents redundant API calls on initial page load
    const operatorStore = useOperatorStore.getState();
    if (!operatorStore.isInitialized) return;

    const currentOperator = operatorStore.operator;
    const urlOperator = params.operator || 'monta';

    // If URL operator differs from current operator, re-initialize
    // This handles dynamic operator changes (e.g., clicking a new operator link)
    if (!currentOperator || currentOperator.identifier !== urlOperator) {
      useOperatorStore.getState().reset();
      void useOperatorStore.getState().initialize();
    }
  }, [params.operator]);

  // Auto-update URL when filters, operator, or selected charge point changes (immediate for better UX)
  useEffect(() => {
    // Don't update URL until after initial params are read
    if (!isInitialized.current) return;

    const debouncedUpdate = debounce(() => {
      setSearchParams(
        (prevParams) => {
          const newParams = new URLSearchParams(prevParams);

          // Locale parameter
          newParams.set('locale', currentLocale);

          // Operator parameter
          // Preserve operator param from URL if operator store is not initialized yet
          const prevOperatorParam = prevParams.get('operator');
          if (operator && operator.identifier !== 'monta') {
            newParams.set('operator', operator.identifier);
          } else if (
            !operator &&
            prevOperatorParam &&
            prevOperatorParam !== 'monta'
          ) {
            // Keep previous operator param if operator store not loaded yet
            newParams.set('operator', prevOperatorParam);
          } else {
            newParams.delete('operator');
          }

          // Charge point parameters
          // Preserve chargePointId/siteId from URL if charge point is still loading
          const prevChargePointId = prevParams.get('chargePointId');
          const prevSiteId = prevParams.get('siteId');

          if (selectedChargePoint) {
            if (selectedChargePoint.type === 'chargePoint') {
              newParams.set(
                'chargePointId',
                selectedChargePoint.chargePoint?.id.toString() || ''
              );
              newParams.delete('siteId');
            } else if (selectedChargePoint.type === 'chargePointGroup') {
              newParams.set(
                'siteId',
                selectedChargePoint.chargePointGroup?.id.toString() || ''
              );
              newParams.delete('chargePointId');
            }
          } else if (isLoadingSelection) {
            // Deep link still loading: keep its params until it resolves
            if (prevChargePointId) {
              newParams.set('chargePointId', prevChargePointId);
            } else {
              newParams.delete('chargePointId');
            }

            if (prevSiteId) {
              newParams.set('siteId', prevSiteId);
            } else {
              newParams.delete('siteId');
            }
          } else {
            // Nothing selected (e.g. closed): drop them. prevParams can be
            // stale here because closeDetails edits history directly, which
            // used to write the closed charge point straight back.
            newParams.delete('chargePointId');
            newParams.delete('siteId');
          }

          // Controls parameter (use 'false' to disable, matching Flutter)
          if (!controlsEnabled) {
            newParams.set('controls', 'false');
          } else {
            newParams.delete('controls');
          }

          // Filter UI parameter
          // Default is closed, only add to URL when open (1)
          if (isFilterPanelOpen) {
            newParams.set('filter_ui', '1');
          } else {
            newParams.delete('filter_ui');
          }

          // Filter Operator UI parameter (default: true, only add if false)
          if (!showOperatorFilter) {
            newParams.set('filter_operator_ui', '0');
          } else {
            newParams.delete('filter_operator_ui');
          }

          // Anchor Point parameter (default: true, only add if false)
          if (!showAnchorPoint) {
            newParams.set('anchor_point', '0');
          } else {
            newParams.delete('anchor_point');
          }

          // Marker Style parameter (default: 'default', only add if non-default)
          if (
            markerStyle === 'operator' ||
            markerStyle === 'operator_neutral'
          ) {
            newParams.set('marker_style', markerStyle);
          } else {
            newParams.delete('marker_style');
          }

          // Update filter params only if they differ from defaults
          // Availability filters
          // Default is showOnlyAvailable=false (show all), only add param if true
          if (showOnlyAvailable) newParams.set('onlyAvailable', '1');
          else newParams.delete('onlyAvailable');

          // Speed filters
          if (!showSlow) newParams.set('slow', '0');
          else newParams.delete('slow');

          if (!showFast) newParams.set('fast', '0');
          else newParams.delete('fast');

          if (!showUltraFast) newParams.set('ultraFast', '0');
          else newParams.delete('ultraFast');

          // Operators (use actual operator IDs for URL, not suggestion IDs)
          // IMPORTANT: Use selectedOperatorIds (e.g., 602) instead of selectedOperatorSuggestionIds (e.g., 44)
          // because operator IDs are global while suggestion IDs are location-dependent
          if (selectedOperatorIds.length > 0) {
            newParams.set('operatorIds', selectedOperatorIds.join(','));
          } else {
            newParams.delete('operatorIds');
          }

          // Team IDs (e.g., 182 for Clever)
          // Team IDs are global and work everywhere
          if (selectedTeamIds.length > 0) {
            newParams.set('teamIds', selectedTeamIds.join(','));
          } else {
            newParams.delete('teamIds');
          }

          // Promotion IDs
          // Promotion IDs are global and work everywhere
          if (selectedPromotionIds.length > 0) {
            newParams.set('promotionIds', selectedPromotionIds.join(','));
          } else {
            newParams.delete('promotionIds');
          }

          // Numeric filters
          if (maxPricePerKwh > 0) {
            newParams.set('maxPrice', maxPricePerKwh.toString());
          } else {
            newParams.delete('maxPrice');
          }

          return newParams;
        },
        { replace: true }
      );
    }, 0); // No debounce - update immediately

    debouncedUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentLocale,
    operator,
    selectedChargePoint,
    isLoadingSelection,
    controlsEnabled,
    isFilterPanelOpen,
    showOperatorFilter,
    showAnchorPoint,
    markerStyle,
    showOnlyAvailable,
    showSlow,
    showFast,
    showUltraFast,
    selectedOperatorIds,
    selectedTeamIds,
    selectedPromotionIds,
    maxPricePerKwh,
  ]);

  return { params, updateUrl };
}
